/**
 * Module: screens/creator/steps/FormatsStep
 * Role: Wizard Step 5/8 — content-format chip-select with `max_formats` cap
 *       (FTR-1131 chrome rewrite). Russian labels via _labels.ts, enum values
 *       persisted in store.
 *
 * Uses:
 *   - react-router-dom:useNavigate
 *   - screens/creator/useCreatorOptions:useCreatorOptions (BUG-1144 auth-gated fetch)
 *   - screens/creator/store:useWizardStore
 *   - screens/creator/_chrome/WizardShell
 *   - screens/creator/_labels:formatLabel
 *   - screens/creator/steps/_ProfileChips:Chips
 *
 * Used by: CreatorOnboardingScreen.tsx
 * ADR-023: no window.Telegram / platform literals outside src/platform/
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreatorOptions } from '../useCreatorOptions';
import { useWizardStore } from '../store';
import { formatLabel } from '../_labels';
import { P, T } from '../../../styles/tokens';
import { Chips } from './_ProfileChips';
import {
  WizardBody, WizardCtaBar, WizardFrame, WizardHeader, WizardStepStrip,
} from '../_chrome/WizardShell';

export default function FormatsStep() {
  const navigate = useNavigate();
  const { draft, setProfile } = useWizardStore();
  const [formats, setFormats] = useState<string[]>(draft.formats);

  const { options, isLoading: optLoading } = useCreatorOptions();

  const max = options?.limits.max_formats ?? 5;

  const items = useMemo(
    () => (options?.formats ?? []).map((v) => ({ value: v, label: formatLabel(v) })),
    [options?.formats],
  );

  const handleBack = () => navigate(-1);

  function handleNext() {
    setProfile({ formats });
    navigate('/creator-onboarding/demographics');
  }

  return (
    <WizardFrame>
      <WizardHeader title="Верификация" onBack={handleBack} />
      <WizardStepStrip current={5} total={8} />
      <WizardBody>
        <div style={{ paddingTop: 10 }}>
          <div style={{ font: T.title2, color: P.ink, marginBottom: 4 }}>
            Форматы
          </div>
          <div style={{ font: T.footnote, color: P.inkSoft }}>
            Что снимаете — до {max} форматов
          </div>
        </div>

        {optLoading && (
          <div style={{ color: P.inkSoft, font: T.subhead }}>Загрузка…</div>
        )}

        {!optLoading && (
          <Chips
            items={items}
            selected={formats}
            max={max}
            onToggle={(f) =>
              setFormats((p) =>
                p.includes(f)
                  ? p.filter((x) => x !== f)
                  : p.length < max
                    ? [...p, f]
                    : p,
              )
            }
          />
        )}
      </WizardBody>
      <WizardCtaBar label="Далее" onClick={handleNext} />
    </WizardFrame>
  );
}
